import { useState } from 'react';
import { useI18n } from '../i18n/index.jsx';

const BRAND_GRADIENT = 'linear-gradient(135deg, #10b981, #8b5cf6)';
const BG_GRADIENT = 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)';

const styles = {
  wrapper: {
    minHeight: '100vh',
    background: BG_GRADIENT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '20px',
    padding: '40px 32px',
    width: '100%',
    maxWidth: '420px',
    backdropFilter: 'blur(20px)',
  },
  logo: {
    textAlign: 'center',
    marginBottom: '8px',
  },
  logoText: {
    fontSize: '28px',
    fontWeight: 800,
    background: BRAND_GRADIENT,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '2px',
  },
  tagline: {
    textAlign: 'center',
    color: 'rgba(255,255,255,0.6)',
    fontSize: '13px',
    marginBottom: '6px',
    lineHeight: 1.5,
  },
  aiTag: {
    textAlign: 'center',
    fontSize: '12px',
    color: 'rgba(255,255,255,0.4)',
    marginBottom: '28px',
  },
  tabs: {
    display: 'flex',
    gap: '4px',
    marginBottom: '24px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '12px',
    padding: '4px',
  },
  tab: (active) => ({
    flex: 1,
    padding: '10px',
    border: 'none',
    borderRadius: '10px',
    background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
    color: active ? '#fff' : 'rgba(255,255,255,0.5)',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  }),
  input: {
    width: '100%',
    padding: '12px 16px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '15px',
    outline: 'none',
    marginBottom: '12px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  btn: {
    width: '100%',
    padding: '14px',
    border: 'none',
    borderRadius: '12px',
    background: BRAND_GRADIENT,
    color: '#fff',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '4px',
    transition: 'opacity 0.2s',
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '20px 0',
    color: 'rgba(255,255,255,0.3)',
    fontSize: '13px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: 'rgba(255,255,255,0.1)',
  },
  tgBtn: {
    width: '100%',
    padding: '14px',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.05)',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  forgot: {
    textAlign: 'right',
    marginBottom: '12px',
  },
  forgotLink: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '13px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
  },
  error: {
    background: 'rgba(239,68,68,0.15)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '10px',
    padding: '10px 14px',
    color: '#f87171',
    fontSize: '13px',
    marginBottom: '12px',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    marginTop: '8px',
    marginBottom: '12px',
    color: 'rgba(255,255,255,0.6)',
    fontSize: '13px',
  },
  row: {
    display: 'flex',
    gap: '12px',
  },
  halfInput: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    display: 'block',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '12px',
    marginBottom: '4px',
    paddingLeft: '4px',
  },
};

export default function WebLogin({ onLogin }) {
  const [tab, setTab] = useState('login');
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Login fields
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register fields
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPassword2, setRegPassword2] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const API_BASE = import.meta.env.VITE_API_URL || 'https://api.workifypl.com';

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      localStorage.setItem('web_jwt', data.access_token);
      sessionStorage.setItem('jwt', data.access_token);
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    setError('');
    if (regPassword !== regPassword2) {
      setError('Пароли не совпадают');
      return;
    }
    if (regPassword.length < 6) {
      setError('Пароль минимум 6 символов');
      return;
    }
    if (!termsAccepted) {
      setError('Примите условия использования');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: regEmail,
          password: regPassword,
          first_name: regFirstName,
          last_name: regLastName,
          phone: regPhone || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Registration failed');
      localStorage.setItem('web_jwt', data.access_token);
      sessionStorage.setItem('jwt', data.access_token);
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <div style={styles.logoText}>WORKIFYPL</div>
        </div>
        <div style={styles.tagline}>
          Управляйте бизнесом голосом — фактуры, кадры, ZUS, налоги
        </div>
        <div style={styles.aiTag}>🤖 AI-powered бухгалтерия для Польши</div>

        <div style={styles.tabs}>
          <button style={styles.tab(tab === 'login')} onClick={() => { setTab('login'); setError(''); }}>
            Вход
          </button>
          <button style={styles.tab(tab === 'register')} onClick={() => { setTab('register'); setError(''); }}>
            Регистрация
          </button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {tab === 'login' ? (
          <form onSubmit={handleLogin}>
            <input
              style={styles.input}
              type="email"
              placeholder="Email"
              value={loginEmail}
              onChange={e => setLoginEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <input
              style={styles.input}
              type="password"
              placeholder="Пароль"
              value={loginPassword}
              onChange={e => setLoginPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <div style={styles.forgot}>
              <button type="button" style={styles.forgotLink} onClick={() => alert('Функция в разработке')}>
                Забыли пароль?
              </button>
            </div>
            <button
              type="submit"
              style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
              disabled={loading}
            >
              {loading ? '⏳ Вход...' : 'Войти'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister}>
            <div style={styles.row}>
              <div style={styles.halfInput}>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="Имя"
                  value={regFirstName}
                  onChange={e => setRegFirstName(e.target.value)}
                  required
                />
              </div>
              <div style={styles.halfInput}>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="Фамилия"
                  value={regLastName}
                  onChange={e => setRegLastName(e.target.value)}
                />
              </div>
            </div>
            <input
              style={styles.input}
              type="email"
              placeholder="Email"
              value={regEmail}
              onChange={e => setRegEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <input
              style={styles.input}
              type="tel"
              placeholder="Телефон (необязательно)"
              value={regPhone}
              onChange={e => setRegPhone(e.target.value)}
            />
            <input
              style={styles.input}
              type="password"
              placeholder="Пароль"
              value={regPassword}
              onChange={e => setRegPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
            <input
              style={styles.input}
              type="password"
              placeholder="Повторите пароль"
              value={regPassword2}
              onChange={e => setRegPassword2(e.target.value)}
              required
              autoComplete="new-password"
            />
            <label style={styles.checkbox}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={e => setTermsAccepted(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>Я принимаю условия использования</span>
            </label>
            <button
              type="submit"
              style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
              disabled={loading}
            >
              {loading ? '⏳ Регистрация...' : 'Зарегистрироваться'}
            </button>
          </form>
        )}

        <div style={styles.divider}>
          <div style={styles.dividerLine} />
          <span>или</span>
          <div style={styles.dividerLine} />
        </div>

        <button
          style={styles.tgBtn}
          onClick={() => window.open('https://t.me/WORKIFYPL_bot', '_blank')}
        >
          <span>✈️</span> Войти через Telegram
        </button>
      </div>
    </div>
  );
}
